import type { WatchSource } from 'vue';
import {
  computed,
  markRaw,
  reactive,
  ref,
  toRaw,
} from 'vue';

import type {
  DrawLayerDeleteDTO,
  DrawLayerFeatureUpdateDTO,
  DrawLayerGeometryPatchDTO,
  DrawLayerResponseDTO,
  DrawLayerTileResponseDTO,
  DrawLayerTilesResponseDTO,
  GeoJsonFeatureDTO,
  OperationDefaultDrawLayerUpdatedEventDTO,
} from '@nora-monorepo/nora-api-client';
import { DrawLayerTypeEnum } from '@nora-monorepo/nora-api-client';
import type { BBox } from '@nora-monorepo/shared';
import {
  FEATURE_TILE_BATCH_MAX,
  isNonEmptyString,
  KnownDrawLayerGeneration,
  MapTile,
} from '@nora-monorepo/shared';
import { downloadTextFile } from '@nora-monorepo/ui-shared';

import type { NoraMap } from '#nora-ui/map/map/NoraMap';
import { OperationBboxStateManager } from '#nora-ui/map/map/state-managers/OperationBboxStateManager';
import { DrawFeatureTileCache } from '#nora-ui/map/map/tiles/draw-feature-tile-cache';
import type { DrawStateSnapshot, Persistable } from '#nora-ui/map/types/persistence.types';
import { useDrawLayerApiStore } from '#nora-ui/stores/api/draw-layer-api-store';
import { useOperationStore } from '#nora-ui/stores/operation-store';

/**
 * Tile data tied to the cache revision it must still match when committed.
 */
interface DrawLayerTileSnapshot {
  tiles: DrawFeatureTileCache;
  revision: number;
}

export class DrawLayerStateManager extends OperationBboxStateManager<DrawLayerTileSnapshot> implements Persistable<DrawStateSnapshot> {
  private visibleDrawLayers = reactive(new Map<string, boolean>());
  private api = useDrawLayerApiStore();
  private operationStore = useOperationStore();

  private drawLayersRef = ref<DrawLayerResponseDTO[]>([]);

  /**
   * Fetched features per (draw layer, tile).
   */
  private tileCache = new DrawFeatureTileCache();
  private tileCacheRevision = 0;
  private featureUpdateQueue = Promise.resolve();

  /**
   * Sequence number of the latest tile load, so older overlapping loads cannot overwrite the
   * cache entries of newer ones.
   */
  private tileLoadSequence = 0;

  get drawLayerMenuOpen(): boolean {
    return this.map.activeSidePanel === 'layers';
  }

  /**
   * The user's explicit draw layer choice as persisted on the map config.
   */
  explicitDrawLayerId = computed((): string | undefined => {
    return this.map.ref.activeDrawLayerId;
  });

  /**
   * The effective draw target: the explicit selection when it belongs to the active
   * operation, otherwise the active operation's default draw layer.
   */
  activeDrawLayer = computed((): DrawLayerResponseDTO | undefined => {
    return this.resolveEffectiveDrawLayer();
  });

  activeDrawLayerId = computed((): string | undefined => {
    return this.activeDrawLayer.value?.id;
  });

  activeDrawLayerLabel = computed((): string | undefined => {
    return this.activeDrawLayer.value?.name;
  });

  hasActiveDrawLayer = computed((): boolean => {
    return this.activeDrawLayer.value !== undefined;
  });

  get drawLayers(): DrawLayerResponseDTO[] {
    return this.drawLayersRef.value;
  }

  areAllDrawLayersVisible = computed((): boolean => {
    return this.drawLayersRef.value.every(drawLayer => this.isDrawLayerVisible(drawLayer.id));
  });

  operationLayerLabel = computed((): string => {
    const labelParts: string[] = [];

    if (this.map.ref.activeOperation) {
      labelParts.push(this.map.ref.activeOperation.name);
    }

    if (isNonEmptyString(this.activeDrawLayerLabel.value)) {
      labelParts.push(this.activeDrawLayerLabel.value);
    }

    if (labelParts.length > 0) {
      return labelParts.join(' - ');
    }

    return 'Layers';
  });

  constructor(map: NoraMap) {
    super(map);
  }

  setDrawLayerMenuOpen(isOpen: boolean): void {
    if (isOpen || this.drawLayerMenuOpen) {
      this.map.setActiveSidePanel(isOpen ? 'layers' : undefined);
    }
  }

  toggleDrawLayerMenuOpen(): void {
    this.setDrawLayerMenuOpen(!this.drawLayerMenuOpen);
  }

  drawLayersForOperation(operationId: string): DrawLayerResponseDTO[] {
    return this.drawLayersRef.value.filter(drawLayer => drawLayer.operationId === operationId);
  }

  async loadDrawLayers(): Promise<void> {
    await Promise.all(
      this.map.ref.visibleOperationIds.map(async operationId => await this.fetchOperationDrawLayers(operationId)),
    );
  }

  /**
   * Loads a single operation's draw layers and features alongside the operations already visible
   */
  override async loadOperation(operationId: string): Promise<void> {
    await this.fetchOperationDrawLayers(operationId);
    await super.loadOperation(operationId);
  }

  /**
   * Fetches the features of an operation's visible draw layers for the map tiles covering
   * the given bbox. No visible layers is a valid, empty result — it must count as a
   * successful state change so the layer still refreshes (e.g. after the operation's last
   * layer is deleted). Only a failed fetch returns undefined, so the checksum stays stale
   * and the next load retries.
   */
  protected override async fetchOperationData(bbox: BBox, operationId: string): Promise<DrawLayerTileSnapshot | undefined> {
    const drawLayerIds = this.visibleDrawLayerIdsForOperation(operationId);

    if (drawLayerIds.length === 0) {
      return { tiles: new DrawFeatureTileCache(), revision: this.tileCacheRevision };
    }

    return await this.fetchOperationTiles(bbox, operationId, drawLayerIds);
  }

  /**
   * Fetches the tiles covering the bbox in batches, sending which tiles are cached and the
   * draw layers' known generations so up-to-date tiles are not sent again, and assembles the
   * draw layers' FeatureCollections from a snapshot of the tile cache that the load keeps up to date,
   * so evictions during the load cannot drop tiles from the result. Returns undefined when
   * any batch failed or the snapshot contains outdated tiles, so the next load retries;
   * the tiles that did load stay cached.
   */
  private async fetchOperationTiles(bbox: BBox, operationId: string, drawLayerIds: string[]): Promise<DrawLayerTileSnapshot | undefined> {
    const tiles = this.featureTilesForBbox(bbox);
    const loadedTiles = this.tileCache.snapshot(tiles, drawLayerIds);
    const revision = this.tileCacheRevision;
    const loadSequence = ++this.tileLoadSequence;
    const results = await Promise.all(this.batchTiles(tiles).map(async batch =>
      await this.fetchTileBatch(batch, operationId, drawLayerIds, loadedTiles, loadSequence)));

    if (results.includes(false) || revision !== this.tileCacheRevision || tiles.some(tile => !loadedTiles.isUpToDate(tile, drawLayerIds))) {
      return undefined;
    }

    return { tiles: loadedTiles, revision };
  }

  /**
   * Splits tiles into batches of at most FEATURE_TILE_BATCH_MAX, one request each.
   */
  private batchTiles(tiles: MapTile[]): MapTile[][] {
    const batches: MapTile[][] = [];

    for (let index = 0; index < tiles.length; index += FEATURE_TILE_BATCH_MAX) {
      batches.push(tiles.slice(index, index + FEATURE_TILE_BATCH_MAX));
    }

    return batches;
  }

  /**
   * Fetches one batch of tiles against the load's snapshot and caches the result.
   * @returns Whether the batch loaded
   */
  private async fetchTileBatch(
    batch: MapTile[],
    operationId: string,
    drawLayerIds: string[],
    loadedTiles: DrawFeatureTileCache,
    loadSequence: number,
  ): Promise<boolean> {
    const revision = this.tileCacheRevision;
    const response = await this.api.getTilesFeatures(
      batch.filter(tile => !loadedTiles.isUpToDate(tile, drawLayerIds)),
      batch.filter(tile => loadedTiles.isUpToDate(tile, drawLayerIds)),
      operationId,
      drawLayerIds.map(drawLayerId => new KnownDrawLayerGeneration(drawLayerId, loadedTiles.knownGeneration(drawLayerId))),
    );

    if (!response || revision !== this.tileCacheRevision) {
      return false;
    }

    for (const [drawLayerId, generation] of Object.entries(response.generations)) {
      if (generation === undefined) {
        continue;
      }

      loadedTiles.setKnownGeneration(drawLayerId, generation);
      this.tileCache.setKnownGeneration(drawLayerId, generation);
    }

    for (const tileResponse of response.tiles) {
      this.cacheTileResponse(tileResponse, response.generations, loadedTiles, loadSequence);
    }

    this.restoreEvictedTiles(batch, drawLayerIds, loadedTiles);

    return true;
  }

  /**
   * Stores the loaded draw layers of a tile response, with the generation they belong to, in
   * the load's snapshot and the tile cache; the cache keeps entries of newer loads.
   */
  private cacheTileResponse(
    response: DrawLayerTileResponseDTO,
    generations: DrawLayerTilesResponseDTO['generations'],
    loadedTiles: DrawFeatureTileCache,
    loadSequence: number,
  ): void {
    const tile = new MapTile(response.z, response.x, response.y);

    for (const [drawLayerId, featureCollection] of Object.entries(response.layers)) {
      const generation = generations[drawLayerId];

      if (!featureCollection || generation === undefined) {
        continue;
      }

      const entry = { generation, features: featureCollection.features, loadSequence };

      loadedTiles.set(drawLayerId, tile, entry);
      this.tileCache.set(drawLayerId, tile, entry);
    }
  }

  /**
   * Writes the snapshot's entries of a batch back to the tile cache, in case the cache evicted
   * them during the load; the cache keeps entries of newer loads either way.
   */
  private restoreEvictedTiles(batch: MapTile[], drawLayerIds: string[], loadedTiles: DrawFeatureTileCache): void {
    for (const tile of batch) {
      for (const drawLayerId of drawLayerIds) {
        const entry = loadedTiles.get(drawLayerId, tile);

        if (entry) {
          this.tileCache.set(drawLayerId, tile, entry);
        }
      }
    }
  }

  /**
   * The feature tiles covering a bbox at the tile zoom for the current view zoom.
   */
  private featureTilesForBbox(bbox: BBox): MapTile[] {
    const viewZoom = this.map.getOlMapOrThrow().getView().getZoom() ?? 0;

    return MapTile.forBBox(bbox, MapTile.featureTileZoomForViewZoom(viewZoom));
  }

  /**
   * Collects features from the committing load's tiles only when the load commits. SSE
   * updates cannot be overwritten by it: one arriving during the load bumps the revision,
   * which discards the load.
   */
  protected override storeOperationData(operationId: string, snapshot: DrawLayerTileSnapshot): boolean {
    if (snapshot.revision !== this.tileCacheRevision) {
      return false;
    }

    const geoJsonByDrawLayerId = snapshot.tiles.collect(
      this.featureTilesForBbox(this.map.getMapBbox()),
      this.visibleDrawLayerIdsForOperation(operationId),
    );

    for (const drawLayer of this.drawLayersForOperation(operationId)) {
      if (!this.isDrawLayerVisible(drawLayer.id)) {
        continue;
      }
      const featureCollection = geoJsonByDrawLayerId[drawLayer.id];

      // markRaw: the features are only read to hand to OpenLayers, so keep them out of Vue's
      // reactive graph — wrapping thousands of them per fetch was a big cost.
      drawLayer.geoJson = markRaw(featureCollection);
    }

    return true;
  }

  /**
   * Removes a released operation's draw layers (including the user's drafts in it) and clears
   * their visibility/active state.
   */
  protected override removeOperationData(operationId: string): boolean {
    const removedLayers = this.drawLayersForOperation(operationId);

    this.drawLayersRef.value = this.drawLayersRef.value.filter(
      drawLayer => drawLayer.operationId !== operationId,
    );

    for (const drawLayer of removedLayers) {
      this.visibleDrawLayers.delete(drawLayer.id);
      this.tileCache.clearDrawLayer(drawLayer.id);

      if (this.map.ref.activeDrawLayerId === drawLayer.id) {
        this.clearActiveDrawLayer();
      }
    }

    return removedLayers.length > 0;
  }

  protected override async refreshLayer(): Promise<void> {
    await this.map.layers.draw.load();
  }

  /**
   * A fetch is determined by the tiles covering the bbox AND which draw layers are visible:
   * pans within the same tiles skip the fetch entirely, while toggling a layer's visibility
   * triggers a refetch even for unchanged tiles.
   */
  protected override getChecksumForBbox(bbox: BBox, operationId: string): string {
    const drawLayerIds = this.visibleDrawLayerIdsForOperation(operationId);
    const tileKeys = this.featureTilesForBbox(bbox).map(tile => tile.key);

    return `${tileKeys.join(',')}|${drawLayerIds.sort().join(',')}`;
  }

  /**
   * Load/reload draw layer state from the API.
   * Clears existing state before loading.
   */
  async load(): Promise<void> {
    this.unload();
    await this.loadDrawLayers();

    // We can make this a fire-and-forget since the draw layer features will load on pan when online, and we don't want to block on this for the rest of the map to load
    this.loadCurrentBbox().catch((error) => {
      console.error('Error loading draw layer features for bbox:', error);
    });
  }

  async loadDrawlayerFeaturesFromState(): Promise<void> {
    await this.map.layers.draw.load();
  }

  /**
   * The ids of the visible draw layers belonging to a given operation.
   */
  private visibleDrawLayerIdsForOperation(operationId: string): string[] {
    return this.drawLayersForOperation(operationId)
      .map(drawLayer => drawLayer.id)
      .filter(id => this.isDrawLayerVisible(id));
  }

  /**
   * Unload/clear all draw layer state. The explicit draw layer choice on the map config
   * is left alone: it is harmless while its layer is unloaded and resolves again on load.
   */
  unload(): void {
    this.tileCacheRevision++;
    this.drawLayersRef.value = [];
    this.visibleDrawLayers.clear();
    this.tileCache.clear();
    this.invalidateBboxChecksums();
  }

  /**
   * Adds a draw layer to the map and reloads its features. Layers of operations not
   * visible on this map are ignored.
   */
  async addDrawLayer(drawLayer: DrawLayerResponseDTO): Promise<void> {
    if (!this.map.ref.isOperationVisible(drawLayer.operationId)) {
      return;
    }

    this.upsertDrawLayer(drawLayer);

    await this.invalidateBboxAndReloadFeatures();
  }

  /**
   * Upserts the created draw layer if its operation is visible on this map, or removes a
   * stale local copy otherwise. Move and publish are also broadcast as create events.
   */
  async handleDrawLayerCreateEvent(data: DrawLayerResponseDTO): Promise<void> {
    if (this.map.ref.isOperationVisible(data.operationId)) {
      this.upsertDrawLayer(data);
    }
    else {
      this.removeDrawLayerFromState(data.id);
    }

    await this.invalidateBboxAndReloadFeatures();
  }

  /**
   * Updates metadata in place, preserving geometry and in-flight feature refreshes.
   */
  async handleDrawLayerUpdateEvent(data: DrawLayerResponseDTO): Promise<void> {
    const drawLayer = this.getDrawLayerById(data.id);

    if (drawLayer) {
      Object.assign(drawLayer, data, { geoJson: drawLayer.geoJson });
    }
  }

  async handleDrawLayerDeleteEvent(data: DrawLayerDeleteDTO): Promise<void> {
    if (!this.getDrawLayerById(data.id)) {
      return;
    }

    this.removeDrawLayerFromState(data.id);

    await this.invalidateBboxAndReloadFeatures();
  }

  /**
   * Queued so an older feature response cannot overwrite a newer update.
   */
  async handleDrawLayerFeatureUpdateEvent(data: DrawLayerFeatureUpdateDTO): Promise<void> {
    const refresh = async (): Promise<void> => await this.refreshUpdatedFeatures(data);

    this.featureUpdateQueue = this.featureUpdateQueue.then(refresh, refresh);

    await this.featureUpdateQueue;
  }

  /**
   * Reloads authoritative changed features without revalidating unrelated tiles or layers.
   */
  private async refreshUpdatedFeatures(data: DrawLayerFeatureUpdateDTO): Promise<void> {
    const drawLayer = this.loadedDrawLayer(data);

    if (data.featureIds.length === 0 || !drawLayer) {
      return;
    }

    const response = await this.api.getDrawLayerFeatures(data.drawLayerId, data.operationId, data.featureIds);

    if (!response) {
      // The revision bump discards a tile load still in flight, so it cannot restore the checksum.
      this.tileCacheRevision++;
      this.invalidateBboxChecksums(data.operationId);

      return;
    }

    if (this.loadedDrawLayer(data) !== drawLayer) {
      return;
    }

    this.tileCacheRevision++;
    const visibleFeatures = this.tileCache.updateFeatures(
      data.drawLayerId,
      data.featureIds,
      response.features,
      this.featureTilesForBbox(this.map.getMapBbox()),
    );

    this.applyGeometryPatchLocally(drawLayer, { remove: data.featureIds, add: visibleFeatures });
    await this.refreshLayer();

    // The revision bump discards a tile load still in flight; reload so its tiles still show.
    await this.loadCurrentBbox();
  }

  /**
   * The event's draw layer, when it is loaded in its visible operation.
   */
  private loadedDrawLayer(data: DrawLayerFeatureUpdateDTO): DrawLayerResponseDTO | undefined {
    const drawLayer = this.getDrawLayerById(data.drawLayerId);

    if (drawLayer?.operationId !== data.operationId || !this.map.ref.isOperationVisible(data.operationId)) {
      return undefined;
    }

    return drawLayer;
  }

  async handleOperationDefaultDrawLayerUpdatedEvent(data: OperationDefaultDrawLayerUpdatedEventDTO): Promise<void> {
    for (const drawLayer of this.drawLayersForOperation(data.operationId)) {
      drawLayer.isDefault = drawLayer.id === data.newDrawLayerId;
    }
  }

  setActiveDrawLayer(drawLayerId: string | undefined): void {
    this.map.ref.setActiveDrawLayer(drawLayerId);

    if (isNonEmptyString(drawLayerId)) {
      this.setDrawLayerVisibility(drawLayerId, true);
    }
  }

  /**
   * Clears the explicit draw layer choice; the effective target then falls back to the
   * active operation's default draw layer via `activeDrawLayer`.
   */
  clearActiveDrawLayer(): void {
    this.map.ref.clearActiveDrawLayer();
  }

  async toggleDrawLayerLock(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);

    if (drawLayer.locked) {
      await this.unlockDrawLayer(drawLayerId);
    }
    else {
      await this.lockDrawLayer(drawLayerId);
    }
  }

  async unlockDrawLayer(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerById(drawLayerId);

    if (drawLayer && drawLayer.locked) {
      await this.api.unlockOperationDrawLayer(drawLayerId, drawLayer.operationId);

      drawLayer.locked = false;
    }
  }

  async lockDrawLayer(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerById(drawLayerId);

    if (drawLayer && !drawLayer.locked) {
      await this.api.lockOperationDrawLayer(drawLayerId, drawLayer.operationId);

      drawLayer.locked = true;
    }
  }

  async setDefaultDrawLayer(drawLayerId: string): Promise<boolean> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);
    const success = await this.api.setDefaultDrawLayer(drawLayerId, drawLayer.operationId);

    if (success) {
      for (const operationDrawLayer of this.drawLayersForOperation(drawLayer.operationId)) {
        operationDrawLayer.isDefault = operationDrawLayer.id === drawLayerId;
      }
    }

    return success;
  }

  isDraftDrawLayer(drawLayerId: string): boolean {
    return this.getDrawLayerById(drawLayerId)?.draft === true;
  }

  reorderDrawLayers(draggedLayerId: string, targetLayerId: string): void {
    const layers = [...this.drawLayersRef.value];
    const draggedIndex = layers.findIndex(layer => layer.id === draggedLayerId);
    const targetIndex = layers.findIndex(layer => layer.id === targetLayerId);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    const [draggedItem] = layers.splice(draggedIndex, 1);
    layers.splice(targetIndex, 0, draggedItem);

    this.drawLayersRef.value = layers;

    this.map.layers.draw.recalculateOperationDrawLayerZIndex();
  }

  async exportDrawLayerById(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);
    const geoJson = await this.api.getOperationDrawLayerGeoJsonById(drawLayerId, drawLayer.operationId);

    if (geoJson) {
      this.downloadGeoJsonAsFile(geoJson, drawLayer.name);
    }
  }

  async updateDrawLayerGeometry(
    patch: DrawLayerGeometryPatchDTO,
    drawLayerId: string,
  ): Promise<void> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);

    await this.api.updateOperationDrawLayerGeometry(
      drawLayerId,
      drawLayer.operationId,
      patch,
    );

    this.applyGeometryPatchLocally(drawLayer, patch);
  }

  /**
   * Folds a geometry patch into the draw layer's own features.
   *
   * Offline there is no SSE echo, so without this a shape drawn offline is missing from the
   * persisted snapshot and gone after a restart.
   *
   * This runs even when the write failed: `updateOperationDrawLayerGeometry` logs and swallows the
   * error, so in the web app a rejected patch stays visible until the next refetch corrects it.
   * Webview mutations always resolve, so the offline path never hits that case.
   */
  private applyGeometryPatchLocally(
    drawLayer: DrawLayerResponseDTO,
    patch: DrawLayerGeometryPatchDTO,
  ): void {
    // Replaced, not mutated: `geoJson` is markRaw'd, so the persistence watcher only sees the
    // property assignment.
    drawLayer.geoJson = markRaw({
      type: 'FeatureCollection',
      features: this.foldGeometryPatch(drawLayer.geoJson?.features ?? [], patch),
    });
  }

  /**
   * Folds a geometry patch into a feature list, keyed by feature id so applying the same patch
   * twice is a no-op.
   * @param existing - the features currently on the draw layer
   * @param patch - the add/update/remove to apply
   * @returns the resulting feature list
   */
  private foldGeometryPatch(
    existing: GeoJsonFeatureDTO[],
    patch: DrawLayerGeometryPatchDTO,
  ): GeoJsonFeatureDTO[] {
    const featuresById = new Map<string, GeoJsonFeatureDTO>(
      existing.map(feature => [feature.id, feature]),
    );

    for (const featureId of patch.remove ?? []) {
      featuresById.delete(featureId);
    }

    for (const feature of [...(patch.add ?? []), ...(patch.update ?? [])]) {
      featuresById.set(feature.id, feature);
    }

    return [...featuresById.values()];
  }

  async deleteDrawLayerById(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);

    await this.api.deleteOperationDrawLayer(drawLayerId, drawLayer.operationId);

    this.removeDrawLayerFromState(drawLayer.id);

    await this.invalidateBboxAndReloadFeatures();
  }

  /**
   * Publish a draft draw layer, making it visible to all operation members.
   */
  async publishDrawLayer(drawLayerId: string): Promise<void> {
    const drawLayer = this.getDrawLayerByIdOrThrow(drawLayerId);

    const publishedDrawLayer = await this.api.publishDrawLayer(drawLayerId, drawLayer.operationId);

    if (publishedDrawLayer) {
      drawLayer.draft = false;
    }
  }

  getDrawLayerById(drawLayerId: string): DrawLayerResponseDTO | undefined {
    return this.drawLayersRef.value.find(drawLayer => drawLayer.id === drawLayerId);
  }

  getDrawLayerByIdOrThrow(drawLayerId: string): DrawLayerResponseDTO {
    const drawLayer = this.getDrawLayerById(drawLayerId);

    if (!drawLayer) {
      throw new Error(`Draw layer with id ${drawLayerId} not found`);
    }

    return drawLayer;
  }

  setDrawLayerVisibility(drawLayerId: string, isVisible: boolean): void {
    this.visibleDrawLayers.set(drawLayerId, isVisible);

    this.map.layers.draw.setDrawLayerVisibility(drawLayerId, isVisible);
  }

  isDrawLayerVisible(drawLayerId: string): boolean {
    return this.visibleDrawLayers.get(drawLayerId) ?? true;
  }

  hideDrawLayer(drawLayerId: string): void {
    this.setDrawLayerVisibility(drawLayerId, false);
  }

  showDrawLayer(drawLayerId: string): void {
    this.setDrawLayerVisibility(drawLayerId, true);
  }

  toggleDrawLayerVisibility(drawLayerId: string): void {
    const currentState = this.isDrawLayerVisible(drawLayerId);
    this.setDrawLayerVisibility(drawLayerId, !currentState);
  }

  toggleAllDrawLayersVisibility(): void {
    if (this.areAllDrawLayersVisible.value) {
      for (const drawLayer of this.drawLayersRef.value) {
        this.hideDrawLayer(drawLayer.id);
      }
    }
    else {
      for (const drawLayer of this.drawLayersRef.value) {
        this.showDrawLayer(drawLayer.id);
      }
    }
  }

  clearAllVisibility(): void {
    this.visibleDrawLayers.clear();
  }

  async invalidateBboxAndReloadFeatures(): Promise<void> {
    this.invalidateBboxChecksums();
    await this.loadCurrentBbox();
  }

  private upsertDrawLayer(drawLayer: DrawLayerResponseDTO): void {
    const index = this.drawLayersRef.value.findIndex(
      existingDrawLayer => existingDrawLayer.id === drawLayer.id,
    );

    if (index === -1) {
      this.drawLayersRef.value.push(drawLayer);
    }
    else {
      this.drawLayersRef.value[index] = drawLayer;
    }
  }

  private removeDrawLayerFromState(drawLayerId: string): void {
    this.drawLayersRef.value = this.drawLayersRef.value.filter(
      drawLayer => drawLayer.id !== drawLayerId,
    );

    this.visibleDrawLayers.delete(drawLayerId);
    this.tileCache.clearDrawLayer(drawLayerId);

    if (this.map.ref.activeDrawLayerId === drawLayerId) {
      this.clearActiveDrawLayer();
    }
  }

  private downloadGeoJsonAsFile(geoJson: object, fileName: string): void {
    downloadTextFile(
      JSON.stringify(geoJson, null, 2),
      `${fileName}.geojson`,
    );
  }

  private async fetchOperationDrawLayers(operationId: string): Promise<void> {
    const operationDrawLayers = await this.operationStore.fetchOperationDrawLayers(operationId);

    // Detach any attached feature graph from Vue reactivity (see storeOperationData).
    for (const drawLayer of operationDrawLayers) {
      if (drawLayer.geoJson) {
        drawLayer.geoJson = markRaw(drawLayer.geoJson);
      }
    }

    // Merge: replace only this operation's layers, leaving other visible operations intact.
    this.drawLayersRef.value = [
      ...this.drawLayersRef.value.filter(drawLayer => drawLayer.operationId !== operationId),
      ...operationDrawLayers,
    ];
  }

  /**
   * Resolve the effective active draw layer, this takes into account:
   * - Whether the user has selected a draw layer manually (explicitDrawLayerId)
   * - Which operations are visible and whether those operations have a default draw layer
   */
  private resolveEffectiveDrawLayer(): DrawLayerResponseDTO | undefined {
    const activeOperationId = this.map.ref.activeOperationId;

    if (activeOperationId === undefined) {
      return undefined;
    }

    const explicitDrawLayerId = this.explicitDrawLayerId.value;

    if (isNonEmptyString(explicitDrawLayerId)) {
      const explicitDrawLayer = this.getDrawLayerById(explicitDrawLayerId);

      if (explicitDrawLayer && explicitDrawLayer.operationId === activeOperationId) {
        return explicitDrawLayer;
      }
    }

    return this.drawLayersForOperation(activeOperationId).find(
      drawLayer => drawLayer.isDefault
        && drawLayer.type !== DrawLayerTypeEnum.GEOFENCE
        && !drawLayer.draft,
    );
  }

  /**
   * Serialize the draw layer state to a plain object for persistence.
   */
  serialize(): DrawStateSnapshot {
    return {
      drawLayers: toRaw(this.drawLayersRef.value),
      visibility: Array.from(this.visibleDrawLayers.entries()),
    };
  }

  /**
   * Restore draw layer state from a persisted snapshot.
   */
  deserialize(snapshot: DrawStateSnapshot): void {
    // Detach persisted feature graphs from Vue reactivity before they enter the ref.
    for (const drawLayer of snapshot.drawLayers) {
      if (drawLayer.geoJson) {
        drawLayer.geoJson = markRaw(drawLayer.geoJson);
      }
    }

    this.drawLayersRef.value = snapshot.drawLayers;

    this.visibleDrawLayers.clear();

    for (const [id, visible] of snapshot.visibility) {
      this.visibleDrawLayers.set(id, visible);
    }
  }

  /**
   * Returns a watch source for triggering persistence when state changes.
   */
  getWatchSource(): WatchSource {
    return () => [
      this.drawLayersRef.value,
      Array.from(this.visibleDrawLayers.entries()),
    ];
  }
}
